import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { SignForm } from '../../models/signForm';
import { Form, Input, Button, Row, message } from 'antd';
import { useHistory } from 'react-router-dom';
import Title from 'antd/lib/typography/Title';
import Link from 'antd/lib/typography/Link';
import { RuleObject } from 'antd/lib/form';
import { postSignInUtilities } from './postSignInUtils';
import { AuthState } from '@aws-amplify/ui-components';
import Text from 'antd/lib/typography/Text';

interface SubmitPasswordResetFormValues {
  Email: string;
}

interface SubmitNewPasswordFormValues {
  resetCode: string;
  newPassword: string;
}

const ResetPassword: React.FC<SignForm> = ({ setAuthState, setUser }) => {
  const [submitting, setSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const history = useHistory();

  const submitPasswordResetRequest = async (
    values: SubmitPasswordResetFormValues
  ): Promise<void> => {
    setSubmitting(true);
    Auth.forgotPassword(values.Email)
      .then(() => {
        setEmail(values.Email);
        setSubmitting(false);
        setRequestSubmitted(true);
      })
      .catch((error) => {
        setSubmitting(false);
        if (error.message !== 'Username/client id combination not found.') {
          message.error(error.message);
        }
      });
  };

  const submitNewPassword = async (
    values: SubmitNewPasswordFormValues
  ): Promise<void> => {
    setSubmitting(true);
    Auth.forgotPasswordSubmit(email, values.resetCode, values.newPassword)
      .then(() => {
        Auth.signIn(email, values.newPassword)
          .then((userInfo) => {
            postSignInUtilities(userInfo);
            setUser(userInfo);
            setAuthState(AuthState.SignedIn);
            setSubmitting(false);

            history.push('/signin');
          })
          .catch((error) => {
            setSubmitting(false);
            setAuthState(AuthState.SignOut);
            message.error(error.message);
          });
      })
      .catch((error) => {
        setSubmitting(false);
        message.error(error.message);
      });
    return;
  };

  const passwordMatchValidator = (
    _: RuleObject,
    value: string,
    callback: (error?: string | undefined) => void
  ): void => {
    if (value !== password) {
      callback('The two passwords that you entered do not match!');
    } else {
      callback();
    }
  };

  return (
    <>
      <Row justify="center">
        <Title level={3}>Reset password</Title>
      </Row>
      {!requestSubmitted && (
        <>
          <Form
            layout="vertical"
            name="basic"
            initialValues={{
              remember: true
            }}
            onFinish={submitPasswordResetRequest}
            style={{ width: '100%' }}
          >
            <Form.Item
              label="Email"
              data-cy="Email"
              name="Email"
              rules={[
                {
                  required: true,
                  message: 'Please input your email!'
                }
              ]}
            >
              <Input size="large" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                data-cy="reset_password"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>

          <Row justify="center">
            <Text>
              Already have an account?{' '}
              <Link onClick={(): void => history.push('/signin')}>Sign in</Link>
            </Text>
          </Row>
          <Row justify="center">
            <Text>
              Don't have an account yet?{' '}
              <Link onClick={(): void => history.push('/signup')}>
                Sign up for free
              </Link>
            </Text>
          </Row>
        </>
      )}
      {requestSubmitted && (
        <>
          <Form
            layout="vertical"
            name="basic"
            initialValues={{
              remember: true
            }}
            onFinish={submitNewPassword}
          >
            <Form.Item
              label="Reset Code"
              data-cy="reset-code"
              name="resetCode"
              rules={[
                {
                  required: true,
                  message:
                    'Please input the reset code you received in your inbox.'
                }
              ]}
            >
              <Input size="large" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="New Password"
              data-cy="newPassword"
              name="newPassword"
              rules={[
                {
                  required: true,
                  message: 'Please specify your new password.'
                }
              ]}
            >
              <Input
                size="large"
                style={{ width: '100%' }}
                type="password"
                onChange={(e): void => setPassword(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label="Repeat Password"
              data-cy="repeatPassword"
              name="repeatPassword"
              rules={[
                {
                  required: true,
                  message: 'Please repeat your new password.'
                },
                {
                  validator: passwordMatchValidator
                }
              ]}
            >
              <Input size="large" style={{ width: '100%' }} type="password" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                data-cy="reset_password"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </>
  );
};

export default ResetPassword;
